import { z } from 'zod'
import { supabaseApi } from '~/supabase/api'
import { AGENT_API_URL } from '../constants'
import { ChatRecordSchema, UsageSchema, type ChatRecord } from './schemas'

const getAccessToken = async (): Promise<string> => {
  const {
    data: { session },
  } = await supabaseApi.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')
  return session.access_token
}

const CreateChatResponseSchema = z.object({ chatId: z.string() })
const ListChatsResponseSchema = z.object({ chats: z.array(ChatRecordSchema) })
const GetChatResponseSchema = z.object({ chat: ChatRecordSchema })

export const wollemAgentApi = {
  createChat: async (): Promise<string> => {
    const token = await getAccessToken()
    const res = await fetch(`${AGENT_API_URL}/api/v1/chats`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error('Failed to create chat session')
    const { chatId } = CreateChatResponseSchema.parse(await res.json())
    return chatId
  },

  listChats: async (): Promise<ChatRecord[]> => {
    const token = await getAccessToken()
    const res = await fetch(`${AGENT_API_URL}/api/v1/chats`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error('Failed to fetch chats')
    const { chats } = ListChatsResponseSchema.parse(await res.json())
    return chats
  },

  getChat: async (chatId: string): Promise<ChatRecord> => {
    const token = await getAccessToken()
    const res = await fetch(`${AGENT_API_URL}/api/v1/chats/${chatId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error('Chat not found')
    const { chat } = GetChatResponseSchema.parse(await res.json())
    return chat
  },

  streamMessage: async (
    chatId: string,
    content: string,
    signal: AbortSignal
  ): Promise<ReadableStreamDefaultReader<Uint8Array>> => {
    const token = await getAccessToken()
    const res = await fetch(`${AGENT_API_URL}/api/v1/chats/${chatId}/messages`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content }),
      signal,
    })
    if (!res.body) throw new Error('No response body')
    return res.body.getReader()
  },

  getUsage: async () => {
    const token = await getAccessToken()
    const res = await fetch(`${AGENT_API_URL}/api/v1/usage`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error('Failed to create chat session')

    return UsageSchema.parse(await res.json())
  },
}
